<script>
import { mapActions, mapState } from 'vuex';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepForwardOutlined,
  StepBackwardOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  ApiOutlined,
  BugOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';

const b = bem('session-player');

export default {
  components: {
    ArrowLeftOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    StepForwardOutlined,
    StepBackwardOutlined,
    DesktopOutlined,
    TabletOutlined,
    MobileOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    ApiOutlined,
    BugOutlined,
  },
  data() {
    return {
      player: null,
      isPlaying: false,
      currentTime: 0,
      totalTime: 0,
      playbackSpeed: 1,
      speedOptions: [0.5, 1, 2, 4, 8],
      activeTab: 'events',
    };
  },
  computed: {
    ...mapState({
      session: (state) => state.analytics.sessionDetail,
      sessionLoading: (state) => state.analytics.sessionDetailLoading,
      sessionEvents: (state) => state.analytics.sessionEvents,
      sessionEventsLoading: (state) => state.analytics.sessionEventsLoading,
    }),
    sessionId() {
      return this.$route.params.id;
    },
    deviceIcon() {
      if (!this.session) return DesktopOutlined;
      switch (this.session.deviceType) {
        case 'mobile':
          return MobileOutlined;
        case 'tablet':
          return TabletOutlined;
        default:
          return DesktopOutlined;
      }
    },
    deviceFrameClass() {
      if (!this.session) return 'desktop';
      return this.session.deviceType || 'desktop';
    },
    playerContainerStyle() {
      if (!this.session) return {};
      const { viewportWidth, deviceType } = this.session;

      if (deviceType === 'mobile') {
        return {
          width: '375px',
          height: '667px',
          transform: 'scale(0.8)',
          transformOrigin: 'top center',
        };
      }
      if (deviceType === 'tablet') {
        return {
          width: '768px',
          height: '1024px',
          transform: 'scale(0.6)',
          transformOrigin: 'top center',
        };
      }
      return {
        width: '100%',
        height: '100%',
        maxWidth: `${viewportWidth || 1200}px`,
      };
    },
    formattedCurrentTime() {
      return this.formatTime(this.currentTime);
    },
    formattedTotalTime() {
      return this.formatTime(this.totalTime);
    },
    progressPercent() {
      if (!this.totalTime) return 0;
      return (this.currentTime / this.totalTime) * 100;
    },
    timelineEvents() {
      if (!this.session) return [];
      const sessionStart = this.session.startedAt
        ? new Date(this.session.startedAt).getTime()
        : 0;
      const events = [];

      // Add page navigations
      (this.session.pages || []).forEach((page) => {
        events.push({
          timestamp: page.timestampStart || new Date(page.enteredAt).getTime(),
          relativeTime: (page.timestampStart || new Date(page.enteredAt).getTime()) - sessionStart,
          type: 'navigation',
          detail: page.path || page.url,
        });
      });

      // Add clicks (rage and dead only — regular clicks are too noisy)
      (this.session.clicks || []).forEach((click) => {
        if (click.clickType === 'rage' || click.clickType === 'dead') {
          events.push({
            timestamp: click.eventTimestamp || new Date(click.clickedAt).getTime(),
            relativeTime: (click.eventTimestamp
              || new Date(click.clickedAt).getTime()) - sessionStart,
            type: click.clickType,
            detail: `${click.elementTag || ''} ${click.elementText || click.elementSelector || ''}`.trim(),
          });
        }
      });

      // Add errors
      (this.session.errors || []).forEach((error) => {
        events.push({
          timestamp: error.eventTimestamp || new Date(error.occurredAt).getTime(),
          relativeTime: (error.eventTimestamp
            || new Date(error.occurredAt).getTime()) - sessionStart,
          type: 'error',
          detail: error.errorMessage || error.errorType || 'Unknown error',
        });
      });

      events.sort((a, c) => a.timestamp - c.timestamp);
      return events;
    },
    pages() {
      if (!this.session?.pages) return [];
      return this.session.pages;
    },
    requests() {
      if (!this.session?.requests) return [];
      return this.session.requests;
    },
    errors() {
      const jsErrors = this.session?.jsErrors || [];
      const apiErrors = this.session?.apiErrors || [];
      return [...jsErrors, ...apiErrors].sort(
        (a, err) => new Date(a.occurredAt || a.createdAt)
          - new Date(err.occurredAt || err.createdAt),
      );
    },
  },
  methods: {
    b,
    ...mapActions('analytics', ['getSessionDetail', 'getSessionEvents', 'clearSessionDetail']),
    formatTime(ms) {
      if (!ms) return '00:00';
      const totalSeconds = Math.floor(ms / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('MMM DD, YYYY HH:mm:ss');
    },
    formatDuration(ms) {
      if (!ms) return '-';
      return `${ms}ms`;
    },
    goBack() {
      this.$router.push('/analytics/sessions');
    },
    async loadSession() {
      await Promise.all([
        this.getSessionDetail(this.sessionId),
        this.getSessionEvents(this.sessionId),
      ]);
      this.initPlayer();
    },
    initPlayer() {
      const events = this.sessionEvents?.events;
      if (!events?.length) return;

      // Dynamic import of rrweb-player v2
      Promise.all([
        import('rrweb-player'),
        import('rrweb-player/dist/style.css'),
      ]).then(([module]) => {
        const RrwebPlayer = module.default || module;

        const container = this.$refs.playerContainer;
        if (!container) return;

        // Clear previous player
        container.innerHTML = '';

        // Use container's actual dimensions so rrweb-player scales the replay down
        const containerRect = container.getBoundingClientRect();
        const width = Math.floor(containerRect.width) || 800;
        const height = Math.floor(containerRect.height) - 10 || 600;

        try {
          this.player = new RrwebPlayer({
            target: container,
            props: {
              events,
              width,
              height,
              autoPlay: false,
              showController: false,
              speed: this.playbackSpeed,
              speedOption: [0.5, 1, 2, 4, 8],
              skipInactive: true,
            },
          });
        } catch (err) {
          console.error('Failed to create rrweb-player:', err);
          return;
        }

        this.player.addEventListener('ui-update-current-time', (event) => {
          this.currentTime = event.payload;
        });

        this.player.addEventListener('ui-update-player-state', (event) => {
          this.isPlaying = event.payload === 'playing';
        });

        // Get total time from replayer metadata or event timestamps
        try {
          const replayer = this.player.getReplayer();
          this.totalTime = replayer.getMetaData().totalTime;
        } catch {
          if (events.length > 1) {
            this.totalTime = events[events.length - 1].timestamp - events[0].timestamp;
          }
        }
      }).catch((err) => {
        console.error('Failed to load rrweb-player:', err);
      });
    },
    togglePlay() {
      if (!this.player) return;
      if (this.isPlaying) {
        this.player.pause();
      } else {
        this.player.play();
      }
    },
    setSpeed(speed) {
      this.playbackSpeed = speed;
      if (this.player) {
        this.player.setSpeed(speed);
      }
    },
    seekTo(percent) {
      if (!this.player || !this.totalTime) return;
      const time = (percent / 100) * this.totalTime;
      this.player.goto(time);
    },
    skipForward() {
      if (!this.player) return;
      const newTime = Math.min(this.currentTime + 10000, this.totalTime);
      this.player.goto(newTime);
    },
    skipBackward() {
      if (!this.player) return;
      const newTime = Math.max(this.currentTime - 10000, 0);
      this.player.goto(newTime);
    },
    jumpToEvent(event) {
      if (!this.player) return;
      const time = event.relativeTime || 0;
      this.player.goto(time);
    },
    getEventIcon(type) {
      switch (type) {
        case 'click':
          return 'cursor';
        case 'input':
          return 'edit';
        case 'scroll':
          return 'swap';
        case 'error':
          return BugOutlined;
        case 'request':
          return ApiOutlined;
        default:
          return InfoCircleOutlined;
      }
    },
    getEventColor(type) {
      switch (type) {
        case 'error': return 'red';
        case 'rage': return 'volcano';
        case 'dead': return 'orange';
        case 'navigation': return 'blue';
        default: return 'default';
      }
    },
    getStatusColor(statusCode) {
      if (!statusCode) return 'default';
      if (statusCode >= 500) return 'red';
      if (statusCode >= 400) return 'orange';
      if (statusCode >= 300) return 'blue';
      return 'green';
    },
  },
  mounted() {
    this.loadSession();
  },
  beforeUnmount() {
    if (this.player) {
      try {
        this.player.pause();
        this.player.$destroy();
      } catch {
        // Player may already be destroyed
      }
      this.player = null;
    }
    this.clearSessionDetail();
  },
};
</script>

<template>
  <div :class="b()">
    <!-- Header -->
    <div :class="b('header')">
      <a-button type="text" @click="goBack">
        <arrow-left-outlined /> Back to Sessions
      </a-button>
      <div v-if="session" :class="b('header-info')">
        <component :is="deviceIcon" style="font-size: 20px; margin-right: 8px" />
        <span>{{ session.browser }} / {{ session.os }}</span>
        <a-divider type="vertical" />
        <span>{{ session.screenWidth }}x{{ session.screenHeight }}</span>
        <a-divider type="vertical" />
        <span>{{ formatDate(session.startedAt) }}</span>
        <a-tag v-if="session.hasErrors" color="red" style="margin-left: 12px">
          <warning-outlined /> Has Errors
        </a-tag>
      </div>
    </div>

    <a-spin :spinning="sessionLoading || sessionEventsLoading">
      <div :class="b('content')">
        <!-- Left Panel - Events/Errors List -->
        <div :class="b('left-panel')">
          <a-tabs v-model:activeKey="activeTab" size="small">
            <a-tab-pane key="events" tab="Events">
              <div :class="b('events-list')">
                <div
                  v-for="(event, index) in timelineEvents"
                  :key="index"
                  :class="b('event-item')"
                  role="button"
                  tabindex="0"
                  @click="jumpToEvent(event)"
                  @keydown.enter="jumpToEvent(event)"
                >
                  <div :class="b('event-time')">{{ formatTime(event.relativeTime) }}</div>
                  <div :class="b('event-content')">
                    <a-tag :color="getEventColor(event.type)" size="small">
                      {{ event.type }}
                    </a-tag>
                    <span :class="b('event-detail')">{{ event.detail }}</span>
                  </div>
                </div>
                <a-empty v-if="!timelineEvents.length" description="No events" />
              </div>
            </a-tab-pane>

            <a-tab-pane key="pages" tab="Pages">
              <div :class="b('pages-list')">
                <div
                  v-for="(page, index) in pages"
                  :key="index"
                  :class="b('page-item')"
                >
                  <div :class="b('page-order')">{{ index + 1 }}</div>
                  <div :class="b('page-content')">
                    <div :class="b('page-title')">{{ page.title || 'Untitled' }}</div>
                    <div :class="b('page-url')">{{ page.path }}</div>
                    <div :class="b('page-duration')">{{ formatDuration(page.durationMs) }}</div>
                  </div>
                </div>
                <a-empty v-if="!pages.length" description="No pages" />
              </div>
            </a-tab-pane>

            <a-tab-pane key="network" tab="Network">
              <div :class="b('network-list')">
                <div
                  v-for="(request, index) in requests"
                  :key="index"
                  :class="b('request-item')"
                >
                  <a-tag :color="getStatusColor(request.statusCode)" size="small">
                    {{ request.statusCode || '-' }}
                  </a-tag>
                  <span :class="b('request-method')">{{ request.method }}</span>
                  <span :class="b('request-url')">{{ request.endpoint }}</span>
                  <span :class="b('request-duration')">{{ request.durationMs }}ms</span>
                </div>
                <a-empty v-if="!requests.length" description="No requests" />
              </div>
            </a-tab-pane>

            <a-tab-pane key="errors" tab="Errors">
              <div :class="b('errors-list')">
                <div
                  v-for="(error, index) in errors"
                  :key="index"
                  :class="b('error-item')"
                >
                  <a-tag color="red" size="small">{{ error.errorType || 'API Error' }}</a-tag>
                  <div :class="b('error-message')">{{ error.errorMessage || error.message }}</div>
                  <div v-if="error.stackTrace" :class="b('error-stack')">
                    {{ error.stackTrace.split('\n')[0] }}
                  </div>
                </div>
                <a-empty v-if="!errors.length" description="No errors" />
              </div>
            </a-tab-pane>
          </a-tabs>
        </div>

        <!-- Right Panel - Player -->
        <div :class="b('right-panel')">
          <div :class="[b('device-frame'), b(`device-frame--${deviceFrameClass}`)]">
            <!-- Mobile Frame -->
            <div v-if="deviceFrameClass === 'mobile'" :class="b('mobile-frame')">
              <div :class="b('mobile-notch')"></div>
              <div :class="b('mobile-screen')">
                <div ref="playerContainer" :class="b('player-container')"></div>
              </div>
              <div :class="b('mobile-home')"></div>
            </div>

            <!-- Tablet Frame -->
            <div v-else-if="deviceFrameClass === 'tablet'" :class="b('tablet-frame')">
              <div :class="b('tablet-screen')">
                <div ref="playerContainer" :class="b('player-container')"></div>
              </div>
            </div>

            <!-- Desktop - No Frame -->
            <div v-else :class="b('desktop-frame')">
              <div ref="playerContainer" :class="b('player-container')"></div>
            </div>
          </div>

          <!-- Player Controls -->
          <div :class="b('controls')">
            <div :class="b('controls-left')">
              <a-button type="text" size="small" @click="skipBackward">
                <step-backward-outlined />
              </a-button>
              <a-button type="primary" shape="circle" @click="togglePlay">
                <pause-circle-outlined v-if="isPlaying" />
                <play-circle-outlined v-else />
              </a-button>
              <a-button type="text" size="small" @click="skipForward">
                <step-forward-outlined />
              </a-button>
            </div>

            <div :class="b('controls-center')">
              <span :class="b('time-display')">
                {{ formattedCurrentTime }} / {{ formattedTotalTime }}
              </span>
              <a-slider
                :value="progressPercent"
                :tooltip-visible="false"
                :class="b('progress-slider')"
                @change="seekTo"
              />
            </div>

            <div :class="b('controls-right')">
              <a-button-group size="small">
                <a-button
                  v-for="speed in speedOptions"
                  :key="speed"
                  :type="playbackSpeed === speed ? 'primary' : 'default'"
                  @click="setSpeed(speed)"
                >
                  {{ speed }}x
                </a-button>
              </a-button-group>
            </div>
          </div>
        </div>
      </div>
    </a-spin>
  </div>
</template>

<style lang="scss">
.session-player {
  min-height: 100vh;
  background: #f5f5f5;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    background: #fff;
    border-bottom: 1px solid #e8e8e8;
  }

  &__header-info {
    display: flex;
    align-items: center;
    color: #666;
    font-size: 14px;
  }

  &__content {
    display: flex;
    height: calc(100vh - 60px);
  }

  &__left-panel {
    width: 350px;
    background: #fff;
    border-right: 1px solid #e8e8e8;
    overflow-y: auto;

    .ant-tabs {
      height: 100%;

      .ant-tabs-content {
        height: calc(100% - 46px);
      }

      .ant-tabs-tabpane {
        height: 100%;
        overflow-y: auto;
      }
    }
  }

  &__events-list,
  &__pages-list,
  &__network-list,
  &__errors-list {
    padding: 8px;
  }

  &__event-item,
  &__page-item,
  &__request-item,
  &__error-item {
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 4px;

    &:hover {
      background: #f5f5f5;
    }
  }

  &__event-time {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }

  &__event-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__event-detail {
    font-size: 12px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__page-order {
    display: inline-block;
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    background: #1890ff;
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    margin-right: 12px;
  }

  &__page-content {
    display: inline-block;
    vertical-align: middle;
  }

  &__page-title {
    font-weight: 500;
  }

  &__page-url {
    font-size: 12px;
    color: #999;
  }

  &__page-duration {
    font-size: 12px;
    color: #1890ff;
  }

  &__request-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__request-method {
    font-weight: 500;
    width: 50px;
  }

  &__request-url {
    flex: 1;
    font-size: 12px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__request-duration {
    font-size: 12px;
    color: #999;
  }

  &__error-message {
    font-size: 13px;
    margin-top: 4px;
  }

  &__error-stack {
    font-size: 11px;
    color: #999;
    font-family: monospace;
    margin-top: 4px;
  }

  &__right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 24px;
    overflow: hidden;
    min-width: 0;
  }

  &__device-frame {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding: 0;
    position: relative;
  }

  &__mobile-frame {
    width: 375px;
    background: #1a1a1a;
    border-radius: 40px;
    padding: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  &__mobile-notch {
    width: 150px;
    height: 30px;
    background: #1a1a1a;
    margin: 0 auto 8px;
    border-radius: 0 0 20px 20px;
  }

  &__mobile-screen {
    background: #fff;
    border-radius: 20px;
    overflow: hidden;
    height: 667px;
  }

  &__mobile-home {
    width: 120px;
    height: 5px;
    background: #666;
    margin: 12px auto 0;
    border-radius: 3px;
  }

  &__tablet-frame {
    background: #1a1a1a;
    border-radius: 20px;
    padding: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  &__tablet-screen {
    background: #fff;
    border-radius: 4px;
    overflow: hidden;
    width: 768px;
    height: 600px;
  }

  &__desktop-frame {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  &__player-container {
    width: 100%;
    height: 100%;

    // rrweb-player v2 generates .rr-player wrapper
    .rr-player {
      width: 100% !important;
      height: 100% !important;
    }

    .rr-player__frame {
      width: 100% !important;
      height: 100% !important;
    }

    iframe {
      border: none;
    }
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 16px 24px;
    background: #fff;
    border-radius: 8px;
    margin-top: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &__controls-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__controls-center {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__time-display {
    font-size: 14px;
    color: #666;
    white-space: nowrap;
  }

  &__progress-slider {
    flex: 1;
  }

  &__controls-right {
    display: flex;
    align-items: center;
  }
}
</style>
